// Faz 5 — fixed-height window math for DataTable's built-in virtualized mode.
//
// The DataTable stays a REAL <table> (caption/thead/tbody semantics, auto
// column layout). A div-based windowing engine (react-window et al.) cannot
// emit valid, column-aligned <tr>s inside a <tbody>, so the built-in mode
// windows the body with top/bottom spacer rows — the exact pattern proven by
// the task inbox (apps/customer-web features/tasks/task-table-window.ts).
// Constants are exported so the CSS custom properties, the scroll math, and
// the tests share one source of truth and cannot drift.

/** Rows below or at this count render exactly as today — no windowing. */
export const DATA_TABLE_WINDOW_THRESHOLD = 100;
/** Pinned row height for every rendered <tr> in windowed mode (px). */
export const DATA_TABLE_ROW_HEIGHT = 56;
/** Pinned sticky table-header height inside the scroll region (px). */
export const DATA_TABLE_HEADER_HEIGHT = 40;
/** Fixed viewport keeps the number of mounted rows predictably bounded (px). */
export const DATA_TABLE_VIEWPORT_HEIGHT = 560;
/** Extra rows above and below the visible slice for smooth scrolling. */
export const DATA_TABLE_OVERSCAN = 8;
/** Narrow viewports scroll horizontally instead of wrapping a pinned row (px). */
export const DATA_TABLE_MIN_INLINE_SIZE = 880;

/** Caller-tunable geometry for {@link DataTableProps.virtualize}. */
export interface DataTableVirtualizeOptions {
  /** Uniform pinned row height in px. Default {@link DATA_TABLE_ROW_HEIGHT}. */
  readonly rowHeight?: number;
  /** Scroll viewport height in px. Default {@link DATA_TABLE_VIEWPORT_HEIGHT}. */
  readonly viewportHeight?: number;
  /** Row count above which windowing engages. Default {@link DATA_TABLE_WINDOW_THRESHOLD}. */
  readonly threshold?: number;
  /** Overscan rows per side. Default {@link DATA_TABLE_OVERSCAN}. */
  readonly overscan?: number;
}

export interface DataTableWindowSlice {
  readonly startIndex: number;
  readonly endIndex: number;
  readonly topSpacerHeight: number;
  readonly bottomSpacerHeight: number;
}

export interface DataTableWindowArgs {
  readonly rowCount: number;
  readonly scrollTop: number;
  readonly rowHeight: number;
  readonly viewportHeight: number;
  readonly overscan: number;
  readonly headerHeight?: number;
}

function maxScrollTop({
  rowCount,
  rowHeight,
  viewportHeight,
  headerHeight = DATA_TABLE_HEADER_HEIGHT,
}: Omit<DataTableWindowArgs, "scrollTop">): number {
  return Math.max(0, headerHeight + rowCount * rowHeight - viewportHeight);
}

/** Keep a stale DOM scroll offset inside the current virtual table geometry. */
export function clampDataTableScrollTop(args: DataTableWindowArgs): number {
  const safeScrollTop = Number.isFinite(args.scrollTop) ? args.scrollTop : 0;
  return Math.min(Math.max(0, safeScrollTop), maxScrollTop(args));
}

/**
 * Slice the table body into a bounded window. The scroll container also holds
 * the sticky header, so only the body portion below that header counts as
 * visible row space.
 */
export function getDataTableWindow(args: DataTableWindowArgs): DataTableWindowSlice {
  const { rowCount, rowHeight, viewportHeight, overscan } = args;
  const headerHeight = args.headerHeight ?? DATA_TABLE_HEADER_HEIGHT;
  const clampedScrollTop = clampDataTableScrollTop(args);
  const bodyScrollTop = Math.max(0, clampedScrollTop - headerHeight);
  const bodyViewportHeight = Math.max(0, viewportHeight - headerHeight);
  const firstVisibleIndex = Math.floor(bodyScrollTop / rowHeight);
  const startIndex = Math.max(0, firstVisibleIndex - overscan);
  const endIndex = Math.min(
    rowCount,
    Math.ceil((bodyScrollTop + bodyViewportHeight) / rowHeight) + overscan,
  );

  return {
    startIndex,
    endIndex,
    topSpacerHeight: startIndex * rowHeight,
    bottomSpacerHeight: (rowCount - endIndex) * rowHeight,
  };
}
